import { HStack } from '@chakra-ui/react';
import { ChangeEvent } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import TextareaAutosize from 'react-textarea-autosize';

import { Box, Show } from 'shared';
import { Flex } from 'shared_DEPRECATED/components/Flex';

import { CoachingToolsCardEditButtons } from 'features/sessions/components';
import {
  SessionsFormCoachingToolsEffectivenessRate,
  SessionsFormCoachingToolsLinkField,
  SessionsFormCoachingToolsMiroBoards,
  SessionsFormCoachingToolsSection,
  SessionsFormCoachingToolsSelector,
} from 'features/sessions/components/Form/CoachingTools';
import { SessionFormCoachingToolCompleted } from 'features/sessions/components/Form/CoachingTools/CardEdit/SessionFormCoachingToolCompleted';
import { COACHING_EXTERNAL_TOOL } from 'features/sessions/config';
import { CoachingToolNote, SessionType } from 'features/sessions/config/types';
import { useSessionUpdateMutation } from 'features/sessions/hooks';
import { isHosCoachingTool } from 'features/sessions/utils';

type SessionsFormCoachingToolsCardEditProps = {
  formKey: string;
  coachingTool: CoachingToolNote;
  onDelete: () => void;
  canBeDeleted?: boolean;
};

export const SessionsFormCoachingToolsCardEdit = ({
  formKey,
  onDelete,
  canBeDeleted = false,
  coachingTool,
}: SessionsFormCoachingToolsCardEditProps) => {
  const { setValue, handleSubmit, reset } = useFormContext<SessionType>();
  const { sessionId } = useParams();
  const { mutate } = useSessionUpdateMutation(sessionId!);

  const note = useWatch({ name: `${formKey}.notes` }) || '';

  const onValueChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    // @ts-ignore
    setValue(`${formKey}.notes`, e.target.value, { shouldDirty: false });
  };

  return (
    <>
      <SessionsFormCoachingToolsSection>
        <HStack gap={3}>
          <Box maxW="500px" w="250px">
            <SessionsFormCoachingToolsSelector
              onSelect={(coachingTool) => {
                // @ts-ignore
                setValue(`${formKey}.tool` as keyof SessionType, coachingTool, {
                  shouldDirty: false,
                });
              }}
              selected={coachingTool.tool.value}
            />
          </Box>
          <Show when={isHosCoachingTool(coachingTool.tool)}>
            <SessionFormCoachingToolCompleted
              formKey={formKey}
              coachingTool={coachingTool}
            />
          </Show>
        </HStack>
      </SessionsFormCoachingToolsSection>
      <Show when={coachingTool.tool.value === COACHING_EXTERNAL_TOOL}>
        <SessionsFormCoachingToolsLinkField formKey={formKey} />
      </Show>
      <SessionsFormCoachingToolsSection title="Session specific notes">
        <TextareaAutosize
          value={note}
          onChange={onValueChange}
          placeholder="Type here"
          style={{
            width: '100%',
            borderRadius: 'var(--border-radius8)',
            border: 'var(--border-secondary)',
            backgroundColor: 'transparent',
            color: 'var(--fgPrimary)',
            boxSizing: 'border-box',
            lineHeight: 'var(--line-height-medium)',
            resize: 'none',
            fontSize: '14px',
            padding: '10px',
            outline: 'var(--fgPrimary)',
            overflow: 'auto',
          }}
          minRows={3}
          maxRows={4}
        />
      </SessionsFormCoachingToolsSection>
      <SessionsFormCoachingToolsSection title="Miro board pdf">
        <SessionsFormCoachingToolsMiroBoards formKey={formKey} />
      </SessionsFormCoachingToolsSection>
      <SessionsFormCoachingToolsSection>
        <SessionsFormCoachingToolsEffectivenessRate
          currentRate={coachingTool.toolEffectivenessRate?.toString() || ''}
          formKey={formKey}
        />
      </SessionsFormCoachingToolsSection>
      <Flex gap="var(--spacing04)">
        <CoachingToolsCardEditButtons
          canBeDeleted={canBeDeleted}
          onDelete={onDelete}
          onSave={async () => {
            setValue(`${formKey}.editMode` as keyof SessionType, false);
            await handleSubmit((data) => mutate(data))();
            reset(undefined, { keepValues: true, keepDirty: false });
          }}
          disabled={!coachingTool.tool.value}
        />
      </Flex>
    </>
  );
};
